import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ajaxCall from '../utils/ajaxcall';
import config from '../config';
import { canSwitchAccounts } from '../config/roles';

const STORAGE_KEY = 'adminSelectedAccountId';

const AccountContext = createContext(null);

export function AccountProvider({ children, token, orgInfo }) {
	const location = useLocation();
	const navigate = useNavigate();
	const [accounts, setAccounts] = useState([]);
	const [accountsLoading, setAccountsLoading] = useState(true);
	const [accountsError, setAccountsError] = useState(null);
	const [selectedAccountId, setSelectedAccountIdState] = useState('');

	const allowAccountSwitch = canSwitchAccounts(orgInfo?.roleId);

	const selectedAccount = useMemo(() => {
		if (!selectedAccountId || !Array.isArray(accounts)) return null;
		return accounts.find((a) => String(a.pk_id) === String(selectedAccountId)) || null;
	}, [accounts, selectedAccountId]);

	const selectedAccountDomain = selectedAccount?.domain || orgInfo?.domain || '';

	const syncUrlWithAccount = useCallback((accountId, { replace = true, clearFilters = false } = {}) => {
		const params = new URLSearchParams(location.search);
		if (accountId) {
			params.set('account', accountId);
		} else {
			params.delete('account');
		}
		if (clearFilters) {
			params.delete('page');
			params.delete('searchQ');
		}
		const search = params.toString();
		navigate(`${location.pathname}${search ? `?${search}` : ''}`, { replace });
	}, [location.pathname, location.search, navigate]);

	const setSelectedAccountId = useCallback((accountId, options = {}) => {
		if (!allowAccountSwitch) {
			return;
		}
		const id = accountId ? String(accountId) : '';
		setSelectedAccountIdState(id);
		if (id) {
			localStorage.setItem(STORAGE_KEY, id);
		} else {
			localStorage.removeItem(STORAGE_KEY);
		}
		syncUrlWithAccount(id, { clearFilters: options.clearFilters !== false });
	}, [allowAccountSwitch, syncUrlWithAccount]);

	useEffect(() => {
		if (!token) {
			setAccounts([]);
			setAccountsLoading(false);
			return;
		}

		let cancelled = false;
		(async () => {
			setAccountsLoading(true);
			const apiCall = await ajaxCall.getCall(
				`${config.api.generic}/account/get-names`,
				{},
				token,
				{ Domain: 'vyayata' }
			);
			if (cancelled) return;

			if (apiCall?.status && Array.isArray(apiCall.context?.message?.data)) {
				setAccounts(apiCall.context.message.data);
				setAccountsError(null);
			} else {
				setAccounts([]);
				setAccountsError('Could not load seller accounts');
			}
			setAccountsLoading(false);
		})();

		return () => { cancelled = true; };
	}, [token]);

	useEffect(() => {
		if (accountsLoading) {
			return;
		}

		if (!allowAccountSwitch) {
			const ownOrgId = orgInfo?.orgId ? String(orgInfo.orgId) : '';
			if (ownOrgId) {
				setSelectedAccountIdState(ownOrgId);
				const urlAccount = new URLSearchParams(location.search).get('account');
				if (urlAccount !== ownOrgId) {
					syncUrlWithAccount(ownOrgId, { replace: true, clearFilters: false });
				}
			}
			return;
		}

		if (!accounts.length) {
			return;
		}

		const urlAccount = new URLSearchParams(location.search).get('account');
		const stored = localStorage.getItem(STORAGE_KEY);
		const valid = (id) => id && accounts.some((a) => String(a.pk_id) === String(id));

		let nextId = '';
		if (valid(urlAccount)) {
			nextId = String(urlAccount);
		} else if (valid(stored)) {
			nextId = String(stored);
		}

		setSelectedAccountIdState(nextId);
		if (nextId && urlAccount !== nextId) {
			syncUrlWithAccount(nextId, { replace: true, clearFilters: false });
		}
	}, [accounts, accountsLoading, allowAccountSwitch, location.search, orgInfo?.orgId, syncUrlWithAccount]);

	const value = useMemo(() => ({
		accounts,
		accountsLoading,
		accountsError,
		selectedAccountId,
		selectedAccount,
		selectedAccountDomain,
		setSelectedAccountId,
		hasSelectedAccount: Boolean(selectedAccountId),
		allowAccountSwitch,
	}), [
		accounts,
		accountsLoading,
		accountsError,
		selectedAccountId,
		selectedAccount,
		selectedAccountDomain,
		setSelectedAccountId,
		allowAccountSwitch,
	]);

	return (
		<AccountContext.Provider value={value}>
			{children}
		</AccountContext.Provider>
	);
}

export function useSelectedAccount() {
	const ctx = useContext(AccountContext);
	if (!ctx) {
		throw new Error('useSelectedAccount must be used within AccountProvider');
	}
	return ctx;
}
